import { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MessageSquareText, Loader2, Send, CalendarClock, Repeat, Users,
  AlertTriangle, XCircle, ChevronDown, ChevronUp, Search, Smartphone, Check, PauseCircle,
  ShieldQuestion, ShieldX, Trash2,
} from "lucide-react";
import {
  buildJoinUrl,
  estimateSmsSegments, renderSmsTemplate, SMS_TR_PRICE_USD,
  SMS_TEMPLATE_MIN_LENGTH, SMS_TEMPLATE_MAX_LENGTH,
  type SmsSegmentType, type SmsScheduleType, type SmsCampaignStatus,
} from "@wedding/shared";
// Etiket/rozet sözlüğü ORTAK — admin `sms-center` çapraz-düğün ekranı aynı
// dosyadan okur, iki ekran asla ayrışmaz. Gerekçe: labels.ts başlığı.
import {
  segmentLabel, scheduleLabel, holdReasonLabel, toHoldReason,
  approvalStatusLabel, toApprovalStatus, campaignStatusLabel, STATUS_BADGE,
} from "./labels";
import type { SmsCampaignsApi, SmsCampaignsProps, SmsTestSendCapability } from "./types";
import { cn } from "./cn";

/**
 * Segment seçici sırası. `readonly SmsSegmentType[]` olduğu için union'dan
 * OLMAYAN bir değer eklenemez; eksik üyeyi ise `segmentLabel`'ın switch'i
 * yakalar (o exhaustive).
 */
const SEGMENT_ORDER: readonly SmsSegmentType[] = [
  "all", "rsvp_none", "rsvp_no", "rsvp_maybe", "manual",
];

/** Guest-facing web app origin — the SAME source both apps already use for
 *  join/QR links (settings.tsx, dashboard.tsx). */
const WEB_BASE = (import.meta.env.VITE_WEB_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "http://localhost:5173";

// ─── Types (API row shapes) ───────────────────────────────────
interface QuotaData {
  used: number; max: number; remaining: number;
  /** F8 — SEGMENT kotası (tur kotasından AYRI, plana bağlı). */
  smsUsed: number; smsMax: number; smsRemaining: number;
  smsChannelEnabled: boolean; smsCampaignsEnabled: boolean;
}
interface CampaignRow {
  id: string; name: string; message_template: string;
  segment_type: SmsSegmentType; schedule_type: SmsScheduleType;
  scheduled_at: string | null; chain_interval_days: number | null;
  chain_max_rounds: number | null; next_dispatch_at: string | null;
  sends_used: number; status: SmsCampaignStatus;
  /** Dolu ise kampanya BEKLEMEDE — `status` tek başına bunu anlatmaz. */
  hold_reason: string | null;
  /** `pending` ise admin onaylayana kadar tek SMS gitmez (047). */
  approval_status: string | null;
  approval_recipient_count: number | null;
  rejection_reason: string | null;
  last_dispatched_at: string | null; created_at: string;
}
interface RoundRow { round_number: number; total: number; sent: number; delivered: number; failed: number; started_at: string }
interface SendRow {
  id: string; guest_name: string | null; phone_e164: string; round_number: number;
  status: string; error_code: string | null; queued_at: string;
}
interface GuestOption { id: string; name: string | null; phone_e164: string }

// ─── Styling shells (guests.tsx ile aynı dil) ─────────────────
const card = "bg-card dark:bg-[rgba(27,24,23,0.75)] dark:backdrop-blur-xl border border-border dark:border-primary/20 shadow-sm rounded-2xl overflow-hidden";
const inputCls = "w-full rounded-xl border border-border bg-muted/30 px-3 py-2.5 text-sm focus:outline-none focus:border-primary/50 focus:bg-background transition-colors";

const SEND_BADGE: Record<string, string> = {
  queued:      "bg-muted/60 text-muted-foreground",
  sent:        "bg-sky-500/10 text-sky-600 dark:text-sky-400",
  delivered:   "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  failed:      "bg-rose-500/10 text-rose-600 dark:text-rose-400",
  undelivered: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
};

const PLACEHOLDERS = ["guest_name", "couple_names", "date", "venue", "rsvp_link"] as const;

// ─── Page ─────────────────────────────────────────────────────
export function SmsCampaignsPage({ slug, wedding, api, locale, testSend, configLink }: SmsCampaignsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Toast (guests.tsx pattern)
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = useCallback((msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Quota + kill-switch banner.
  // ⚠️ Bu yanıt admin kill switch'lerini (smsChannelEnabled / smsCampaignsEnabled)
  // taşıyor → app'lerin global `staleTime: 300_000` + `refetchOnWindowFocus: false`
  // varsayılanını BURADA ezmek zorundayız. Aksi halde admin "SMS Kampanyaları"nı
  // açtıktan sonra bu sayfa 5 dakika boyunca "devre dışı" göstermeye devam eder
  // (kullanıcı bunu bildirdi: yalnız hard refresh ile düzeliyordu).
  const { data: quota, isLoading: quotaLoading } = useQuery({
    queryKey: ["sms-quota", slug],
    queryFn: async () => (await api.get<QuotaData>("/sms-campaigns/quota")).data as QuotaData,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });

  // Campaign list (cursor-paginated, limit 50 convention)
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(true);
  // Sayfa gate'i yalnız İLK (cursor'suz) çağrıyı bekler — "Load more" tüm
  // sayfayı skeleton'a düşürmemeli.
  const [initialListLoaded, setInitialListLoaded] = useState(false);

  const loadCampaigns = useCallback(async (cursor?: string) => {
    setListLoading(true);
    try {
      const res = await api.get<{ items: CampaignRow[]; nextCursor: string | null }>(
        cursor ? `/sms-campaigns?cursor=${encodeURIComponent(cursor)}` : "/sms-campaigns",
      );
      const d = res.data as { items: CampaignRow[]; nextCursor: string | null };
      setCampaigns((prev) => (cursor ? [...prev, ...d.items] : d.items));
      setNextCursor(d.nextCursor);
    } catch (err) {
      showToast(err instanceof Error ? err.message : t("common.error"), false);
    } finally {
      setListLoading(false);
      if (!cursor) setInitialListLoaded(true);
    }
  }, [api, showToast, t]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  // ─── Segment state — Composer'dan BURAYA taşındı ────────────────────────────
  // Tavuk-yumurta: state Composer'da kalırsa Composer gate'i geçmeden mount
  // olmaz → segment-preview effect'i hiç ateşlenmez → latch hiç `true` olmaz →
  // sonsuz skeleton. Sayfa fetch edip prop geçince döngü kırılır (quota ile
  // simetrik — yeni desen değil).
  const [segmentType, setSegmentType] = useState<SmsSegmentType>("all");
  const [manualIds, setManualIds] = useState<Set<string>>(new Set());
  const manualIdsKey = useMemo(() => [...manualIds].sort().join(","), [manualIds]);
  const [segmentCount, setSegmentCount] = useState<number | null>(null);
  const [excludedNoConsent, setExcludedNoConsent] = useState(0);
  // Bir kerelik latch — İLK yanıtta (başarı VEYA hata) true olur ve bir daha
  // ASLA false'a dönmez. Sonraki segment değişimlerinde Composer'ın kendi "…"
  // placeholder'ı devreye girer; tüm sayfa yeniden skeleton'a düşmez.
  const [initialSegmentSettled, setInitialSegmentSettled] = useState(false);

  // Segment preview count (server = tek kaynak; dispatch ile aynı sorgu)
  useEffect(() => {
    let stale = false;
    const settle = () => { if (!stale) setInitialSegmentSettled(true); };
    if (segmentType === "manual" && !manualIdsKey) {
      setSegmentCount(0); setExcludedNoConsent(0); settle();
      return;
    }
    const params = new URLSearchParams({ segment_type: segmentType });
    if (segmentType === "manual") params.set("guest_ids", manualIdsKey);
    api.get<{ count: number; excludedNoConsent: number }>(`/sms-campaigns/segment-preview?${params.toString()}`)
      .then((res) => {
        if (stale) return;
        const d = res.data as { count: number; excludedNoConsent: number };
        setSegmentCount(d.count);
        setExcludedNoConsent(d.excludedNoConsent ?? 0);
      })
      .catch(() => { if (!stale) setSegmentCount(null); })
      .finally(settle);
    return () => { stale = true; };
  }, [api, segmentType, manualIdsKey]);

  const refreshAll = useCallback(() => {
    loadCampaigns();
    queryClient.invalidateQueries({ queryKey: ["sms-quota", slug] });
  }, [loadCampaigns, queryClient, slug]);

  // Master kill-switch (sms_enabled) OTP'yi de kapatır — ayrı, daha ciddi bir
  // uyarı metni hak eder. Kampanya rollout kapısı (sms_campaigns_enabled) yalnız
  // bu özelliği etkiler; ikisi de gönderimi durdurur ama nedeni farklıdır.
  const disabledReason: "channel" | "campaigns" | null = !quota
    ? null
    : !quota.smsChannelEnabled ? "channel"
    : !quota.smsCampaignsEnabled ? "campaigns"
    : null;
  const channelDisabled = disabledReason !== null;

  // Sayfa TAMAMEN hazır olmadan hiçbir şey gösterme — alanların parça parça
  // belirmesi yerine tek parça, layout'la hizalı bir skeleton.
  const pageReady = !quotaLoading && initialListLoaded && initialSegmentSettled;
  if (!pageReady) return <SmsPageSkeleton />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif italic text-3xl text-foreground">{t("sms.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("sms.description")}</p>
        </div>
        {quota && (
          // İKİ kota AYRI gösterilir: tur = "kaç KEZ", SMS adedi = "kaç segment".
          // Biri dolup diğeri müsait olabilir; tek rozet yanıltıcı olurdu.
          <div className="flex items-center gap-3">
            <div className="shrink-0 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-2.5 text-center">
              <p className="text-2xl font-black text-primary tabular-nums">{quota.remaining}<span className="text-sm font-semibold text-primary/60">/{quota.max}</span></p>
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("sms.quotaLabel")}</p>
            </div>
            <div className="shrink-0 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-2.5 text-center">
              <p className="text-2xl font-black text-primary tabular-nums">{quota.smsRemaining}<span className="text-sm font-semibold text-primary/60">/{quota.smsMax}</span></p>
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("sms.smsQuotaLabel")}</p>
            </div>
          </div>
        )}
      </div>

      {/* Admin kill-switch banner */}
      {disabledReason && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3.5">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-700 dark:text-amber-400">
            <p>{t(disabledReason === "channel" ? "sms.channelDisabled" : "sms.campaignsDisabled")}</p>
            {/* Ayarı değiştirebilecek host (admin) bir kısayol geçer; organizer geçmez. */}
            {configLink && <div className="mt-2">{configLink}</div>}
          </div>
        </div>
      )}

      <Composer
        api={api}
        quotaRemaining={quota?.remaining ?? 0}
        smsQuotaRemaining={quota?.smsRemaining ?? 0}
        channelDisabled={channelDisabled}
        coupleNames={wedding?.title ?? ""}
        weddingDate={wedding?.date_time ?? null}
        venue={(wedding as { location?: string } | null)?.location ?? ""}
        slug={slug}
        locale={locale}
        testSend={testSend}
        segmentType={segmentType}
        setSegmentType={setSegmentType}
        manualIds={manualIds}
        setManualIds={setManualIds}
        segmentCount={segmentCount}
        excludedNoConsent={excludedNoConsent}
        onCreated={() => { refreshAll(); }}
        showToast={showToast}
      />

      <CampaignList
        api={api}
        slug={slug}
        campaigns={campaigns}
        loading={listLoading}
        nextCursor={nextCursor}
        onLoadMore={() => nextCursor && loadCampaigns(nextCursor)}
        onCanceled={refreshAll}
        showToast={showToast}
        locale={locale}
      />

      {/* Toast */}
      {toast && (
        <div role="status" aria-live="polite" className={cn(
          "fixed bottom-24 md:bottom-8 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-2xl shadow-lg text-sm font-medium text-white",
          toast.ok ? "bg-emerald-600" : "bg-rose-600",
        )}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ─── Full-page skeleton ───────────────────────────────────────
// Gerçek layout'la hizalı (layout shift yok): boyutlar gerçek karşılıklarından
// ölçüldü, kota rozeti + geçmiş satırları mevcut skeleton JSX'lerinin kopyası.
function SmsPageSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-2">
          <div className="h-8 w-56 rounded-xl bg-muted animate-pulse" />
          <div className="h-4 w-72 rounded-md bg-muted animate-pulse" />
        </div>
        <div className="shrink-0 rounded-2xl border border-border bg-muted/30 px-4 py-2.5 text-center space-y-1.5">
          <div className="h-6 w-14 mx-auto rounded-md bg-muted animate-pulse" />
          <div className="h-2.5 w-16 mx-auto rounded-md bg-muted animate-pulse" />
        </div>
      </div>

      <div className={card}>
        <div className="px-5 py-3.5 border-b border-border bg-muted/30">
          <div className="h-3 w-32 rounded-md bg-muted animate-pulse" />
        </div>
        <div className="p-5 space-y-5">
          <div className="space-y-1.5">
            <div className="h-3 w-24 rounded-md bg-muted animate-pulse" />
            <div className="h-10 w-full rounded-xl bg-muted animate-pulse" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <div className="h-3 w-28 rounded-md bg-muted animate-pulse" />
              <div className="h-28 w-full rounded-xl bg-muted animate-pulse" />
            </div>
            <div className="space-y-1.5">
              <div className="h-3 w-20 rounded-md bg-muted animate-pulse" />
              <div className="h-28 w-full rounded-xl bg-muted animate-pulse" />
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-3 w-40 rounded-md bg-muted animate-pulse" />
            <div className="flex flex-wrap gap-2">
              {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-9 w-24 rounded-xl bg-muted animate-pulse" />)}
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-3 w-44 rounded-md bg-muted animate-pulse" />
            <div className="flex flex-wrap gap-2">
              {[0, 1, 2].map((i) => <div key={i} className="h-9 w-32 rounded-xl bg-muted animate-pulse" />)}
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2 border-t border-border">
            <div className="h-9 w-28 rounded-xl bg-muted animate-pulse" />
            <div className="ml-auto h-10 w-32 rounded-xl bg-muted animate-pulse" />
          </div>
        </div>
      </div>

      <div className={card}>
        <div className="px-5 py-3.5 border-b border-border bg-muted/30">
          <div className="h-3 w-36 rounded-md bg-muted animate-pulse" />
        </div>
        <div className="divide-y divide-border">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 px-5 py-4">
              <div className="flex-1 min-w-40 space-y-2">
                <div className="h-3.5 w-32 rounded-md bg-muted animate-pulse" />
                <div className="h-2.5 w-48 rounded-md bg-muted animate-pulse" />
              </div>
              <div className="h-2.5 w-10 rounded-md bg-muted animate-pulse" />
              <div className="h-5 w-16 rounded-full bg-muted animate-pulse" />
              <div className="h-6 w-6 rounded-lg bg-muted animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Composer ─────────────────────────────────────────────────
function Composer({
  api, quotaRemaining, smsQuotaRemaining, channelDisabled, coupleNames, weddingDate, venue, slug, locale, testSend,
  segmentType, setSegmentType, manualIds, setManualIds, segmentCount, excludedNoConsent,
  onCreated, showToast,
}: {
  api: SmsCampaignsApi;
  quotaRemaining: number; smsQuotaRemaining: number; channelDisabled: boolean;
  coupleNames: string; weddingDate: string | null; venue: string;
  slug: string; locale: string; testSend: SmsTestSendCapability;
  // Segment state sayfada yaşar (tam-sayfa gate'i tavuk-yumurta'ya düşmesin).
  segmentType: SmsSegmentType; setSegmentType: (s: SmsSegmentType) => void;
  manualIds: Set<string>; setManualIds: (next: Set<string>) => void;
  segmentCount: number | null; excludedNoConsent: number;
  onCreated: () => void; showToast: (msg: string, ok?: boolean) => void;
}) {
  const { t } = useTranslation();

  const [name, setName] = useState("");
  const [template, setTemplate] = useState("");
  const [scheduleType, setScheduleType] = useState<SmsScheduleType>("now");
  const [scheduledAt, setScheduledAt] = useState("");
  const [chainDays, setChainDays] = useState(3);
  const [chainMaxRounds, setChainMaxRounds] = useState(2);
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);

  // Default template once translations are ready
  useEffect(() => {
    setTemplate((prev) => prev || t("sms.defaultTemplate"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live preview + cost estimate
  const rendered = useMemo(() => renderSmsTemplate(template, {
    guest_name: t("sms.sampleGuest"),
    couple_names: coupleNames,
    date: weddingDate
      ? new Date(weddingDate).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" })
      : "",
    venue,
    // Sub-domain string replace ("organizer." → "") admin'de HİÇBİR ŞEY yapmaz
    // (admin.wedding...) → önizleme misafirin görmeyeceği domaini gösterirdi.
    // Repo'nun canonical çözümü: VITE_WEB_BASE_URL + buildJoinUrl. Yalnız
    // EKRANDAKİ önizlemeyi etkiler — gerçek gönderim server-side (sms-dispatch).
    rsvp_link: buildJoinUrl(WEB_BASE, slug),
  }), [template, coupleNames, weddingDate, venue, slug, locale, t]);

  const est = useMemo(() => estimateSmsSegments(rendered), [rendered]);
  const costUsd = segmentCount != null ? (segmentCount * est.segments * SMS_TR_PRICE_USD).toFixed(2) : null;

  const templateValid = template.trim().length >= SMS_TEMPLATE_MIN_LENGTH && template.length <= SMS_TEMPLATE_MAX_LENGTH;
  const canSubmit =
    !submitting && !channelDisabled && quotaRemaining > 0 && smsQuotaRemaining > 0 && templateValid && name.trim().length > 0 &&
    (segmentType !== "manual" || manualIds.size > 0) &&
    (scheduleType === "now" || scheduledAt !== "");

  const insertPlaceholder = (ph: string) => setTemplate((prev) => `${prev}{{${ph}}}`);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        name: name.trim(),
        messageTemplate: template,
        segmentType,
        scheduleType,
      };
      if (segmentType === "manual") body.segmentGuestIds = [...manualIds];
      if (scheduleType !== "now") body.scheduledAt = new Date(scheduledAt).toISOString();
      if (scheduleType === "auto_chain") {
        body.chainIntervalDays = chainDays;
        body.chainMaxRounds = chainMaxRounds;
      }
      const res = await api.post<{
        id: string; warning: string | null;
        approvalPending?: boolean; recipientCount?: number | null;
      }>("/sms-campaigns", body);
      const data = res.data as
        { warning: string | null; approvalPending?: boolean; recipientCount?: number | null } | undefined;
      const warning = data?.warning;
      // Onay AYRI bir alandan okunur (`warning` yalnız SmsGateReason anlatır).
      // Organizatör kararı ANINDA görür — "gidiyor" sanıp sonra sessizce
      // beklemede kalması 27 Tem 2026 olayının birebir tekrarı olurdu.
      const approvalMsg = data?.approvalPending
        ? t("sms.approval.createdPending", { count: data.recipientCount ?? 0 })
        : null;
      showToast(approvalMsg ?? warning ?? t("sms.created"), !approvalMsg && !warning);
      setName("");
      setManualIds(new Set());
      onCreated();
    } catch (err) {
      showToast(err instanceof Error ? err.message : t("common.error"), false);
    } finally {
      setSubmitting(false);
    }
  };

  // Test SMS'i backend'de organizatörün DOĞRULANMIŞ kendi numarasına gider
  // (elle numara girişi kaldırıldı — keyfi-alıcı relay kapatıldı).
  const sendTest = async () => {
    if (testing || !templateValid) return;
    setTesting(true);
    try {
      await api.post("/sms-campaigns/preview-send", { messageTemplate: template });
      showToast(t("sms.testSent"));
    } catch (err) {
      showToast(err instanceof Error ? err.message : t("common.error"), false);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className={card}>
      <div className="px-5 py-3.5 border-b border-border bg-muted/30 flex items-center gap-2">
        <MessageSquareText className="h-3.5 w-3.5 text-primary/60" />
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("sms.newCampaign")}</p>
        {/* Hangi kota tükendiyse SPESİFİK mesaj — "gönderemezsiniz" demek
            yetmez, organizatör NEYİ yükseltmesi gerektiğini bilmeli. */}
        {quotaRemaining <= 0 && (
          <span className="ml-auto text-xs font-semibold text-rose-500">{t("sms.quotaExhausted")}</span>
        )}
        {quotaRemaining > 0 && smsQuotaRemaining <= 0 && (
          <span className="ml-auto text-xs font-semibold text-rose-500">{t("sms.smsQuotaExhausted")}</span>
        )}
      </div>

      <div className="p-5 space-y-5">
        {/* Name */}
        <div>
          <label htmlFor="sms-name" className="block text-xs font-semibold text-muted-foreground mb-1.5">{t("sms.nameLabel")}</label>
          <input id="sms-name" value={name} onChange={(e) => setName(e.target.value)}
            maxLength={100} placeholder={t("sms.namePlaceholder")} className={inputCls} />
        </div>

        {/* Template + live preview */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="sms-template" className="block text-xs font-semibold text-muted-foreground mb-1.5">{t("sms.templateLabel")}</label>
            <textarea id="sms-template" value={template} onChange={(e) => setTemplate(e.target.value)}
              rows={5} maxLength={SMS_TEMPLATE_MAX_LENGTH} className={cn(inputCls, "resize-y min-h-28")} />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {PLACEHOLDERS.map((ph) => (
                <button key={ph} type="button" onClick={() => insertPlaceholder(ph)}
                  className="px-2 py-1 rounded-lg bg-primary/10 text-primary text-[11px] font-mono font-semibold hover:bg-primary/20 transition-colors cursor-pointer">
                  {`{{${ph}}}`}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-1.5">{t("sms.previewLabel")}</p>
            <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-foreground whitespace-pre-wrap min-h-28">
              {rendered || <span className="text-muted-foreground">{t("sms.previewEmpty")}</span>}
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground tabular-nums">
              {t("sms.segmentInfo", { chars: est.length, segments: est.segments })}
              {est.encoding === "ucs2" && <span className="ml-1 text-amber-600 dark:text-amber-400">{t("sms.ucs2Hint")}</span>}
            </p>
          </div>
        </div>

        {/* Segment picker */}
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-2">{t("sms.segmentLabel")}</p>
          <div className="flex flex-wrap gap-2">
            {SEGMENT_ORDER.map((s) => (
              <button key={s} type="button" onClick={() => setSegmentType(s)}
                className={cn(
                  "px-3.5 py-2 rounded-xl text-xs font-semibold border transition-colors cursor-pointer",
                  segmentType === s
                    ? "bg-primary text-white border-primary"
                    : "bg-muted/30 text-muted-foreground border-border hover:border-primary/40 hover:text-foreground",
                )}>
                {segmentLabel(t, s)}
              </button>
            ))}
            <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-muted/50 text-xs font-semibold text-foreground tabular-nums">
              <Users className="h-3.5 w-3.5 text-primary/60" />
              {segmentCount != null ? t("sms.recipientCount", { count: segmentCount }) : "…"}
            </span>
            {excludedNoConsent > 0 && (
              <span className="inline-flex items-center px-3 py-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-semibold tabular-nums">
                {t("sms.excludedNoConsent", { count: excludedNoConsent })}
              </span>
            )}
          </div>
          {segmentType === "manual" && (
            <ManualGuestPicker api={api} slug={slug} selected={manualIds} onChange={setManualIds} />
          )}
        </div>

        {/* Schedule */}
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-2">{t("sms.scheduleLabel")}</p>
          <div className="flex flex-wrap gap-2">
            {([
              { v: "now" as const, icon: Send, label: t("sms.schedules.now") },
              { v: "scheduled" as const, icon: CalendarClock, label: t("sms.schedules.scheduled") },
              { v: "auto_chain" as const, icon: Repeat, label: t("sms.schedules.auto_chain") },
            ]).map(({ v, icon: Icon, label }) => (
              <button key={v} type="button" onClick={() => setScheduleType(v)}
                className={cn(
                  "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold border transition-colors cursor-pointer",
                  scheduleType === v
                    ? "bg-primary text-white border-primary"
                    : "bg-muted/30 text-muted-foreground border-border hover:border-primary/40 hover:text-foreground",
                )}>
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
          {scheduleType !== "now" && (
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <div>
                <label htmlFor="sms-scheduled-at" className="block text-[11px] font-semibold text-muted-foreground mb-1">{t("sms.scheduledAtLabel")}</label>
                <input id="sms-scheduled-at" type="datetime-local" value={scheduledAt}
                  min={new Date(Date.now() + 5 * 60_000).toISOString().slice(0, 16)}
                  onChange={(e) => setScheduledAt(e.target.value)} className={cn(inputCls, "w-auto")} />
              </div>
              {scheduleType === "auto_chain" && (
                <>
                  <div>
                    <label htmlFor="sms-chain-days" className="block text-[11px] font-semibold text-muted-foreground mb-1">{t("sms.chainDaysLabel")}</label>
                    <input id="sms-chain-days" type="number" min={1} max={30} value={chainDays}
                      onChange={(e) => setChainDays(Math.max(1, Math.min(30, Number(e.target.value) || 1)))}
                      className={cn(inputCls, "w-24")} />
                  </div>
                  <div>
                    <label htmlFor="sms-chain-rounds" className="block text-[11px] font-semibold text-muted-foreground mb-1">{t("sms.chainRoundsLabel")}</label>
                    <input id="sms-chain-rounds" type="number" min={1} max={5} value={chainMaxRounds}
                      onChange={(e) => setChainMaxRounds(Math.max(1, Math.min(5, Number(e.target.value) || 1)))}
                      className={cn(inputCls, "w-24")} />
                  </div>
                  <p className="text-[11px] text-muted-foreground pb-2.5 basis-full">{t("sms.chainHint")}</p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer: test send + cost + submit */}
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border">
          <div className="flex items-center gap-2 flex-wrap">
            <Smartphone className="h-4 w-4 text-muted-foreground shrink-0" />
            <button type="button" onClick={sendTest}
              disabled={testing || channelDisabled || !templateValid || !testSend.enabled}
              title={!testSend.enabled ? t(testSend.disabledReasonKey!) : undefined}
              className="px-3.5 py-2.5 rounded-xl border border-border text-xs font-semibold text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
              {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t("sms.sendTest")}
            </button>
            <span className="text-[11px] text-muted-foreground">
              {testSend.enabled ? t("sms.testHint") : t(testSend.disabledReasonKey!)}
            </span>
          </div>

          <div className="ml-auto flex items-center gap-4">
            {channelDisabled && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                {t("sms.sendDisabledHint")}
              </span>
            )}
            {costUsd != null && segmentCount != null && segmentCount > 0 && (
              <p className="text-xs text-muted-foreground tabular-nums">
                {t("sms.costEstimate", { cost: costUsd })}
              </p>
            )}
            <button type="button" onClick={submit} disabled={!canSubmit}
              title={channelDisabled ? t("sms.sendDisabledHint") : undefined}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {scheduleType === "now" ? t("sms.submitNow") : t("sms.submitSchedule")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Manual guest picker ──────────────────────────────────────
function ManualGuestPicker({ api, slug, selected, onChange }: {
  api: SmsCampaignsApi; slug: string;
  selected: Set<string>; onChange: (next: Set<string>) => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const { data: guests = [], isLoading } = useQuery({
    queryKey: ["sms-guest-options", slug],
    queryFn: async () => {
      const res = await api.get<{ guests?: GuestOption[]; items?: GuestOption[] } | GuestOption[]>("/guests?limit=500");
      const d = res.data as { guests?: GuestOption[]; items?: GuestOption[] } | GuestOption[];
      return Array.isArray(d) ? d : (d.guests ?? d.items ?? []);
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return guests;
    return guests.filter((g) => (g.name ?? "").toLowerCase().includes(q) || g.phone_e164.includes(q));
  }, [guests, search]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(next);
  };

  return (
    <div className="mt-3 rounded-xl border border-border overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/20">
        <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={t("sms.searchGuests")} aria-label={t("sms.searchGuests")} spellCheck={false}
          className="flex-1 bg-transparent text-sm rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
        <span className="text-[11px] font-semibold text-muted-foreground tabular-nums">
          {t("sms.selectedCount", { count: selected.size })}
        </span>
      </div>
      <div className="max-h-56 overflow-y-auto divide-y divide-border">
        {isLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">{t("sms.noGuests")}</p>
        ) : (
          filtered.map((g) => (
            <button key={g.id} type="button" onClick={() => toggle(g.id)}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-primary/5 transition-colors cursor-pointer">
              <span className={cn(
                "h-4 w-4 shrink-0 rounded border flex items-center justify-center",
                selected.has(g.id) ? "bg-primary border-primary" : "border-border",
              )}>
                {selected.has(g.id) && <Check className="h-3 w-3 text-white" />}
              </span>
              <span className="text-sm text-foreground flex-1 truncate">{g.name ?? "—"}</span>
              <span className="text-xs font-mono text-muted-foreground">{g.phone_e164}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Campaign list + detail ───────────────────────────────────
function CampaignList({ api, slug, campaigns, loading, nextCursor, onLoadMore, onCanceled, showToast, locale }: {
  api: SmsCampaignsApi; slug: string;
  campaigns: CampaignRow[]; loading: boolean; nextCursor: string | null;
  onLoadMore: () => void; onCanceled: () => void;
  showToast: (msg: string, ok?: boolean) => void; locale: string;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [canceling, setCanceling] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const cancel = async (id: string) => {
    if (canceling) return;
    setCanceling(id);
    try {
      await api.post(`/sms-campaigns/${id}/cancel`);
      showToast(t("sms.canceled"));
      onCanceled();
    } catch (err) {
      showToast(err instanceof Error ? err.message : t("common.error"), false);
    } finally {
      setCanceling(null);
    }
  };

  // Silme yalnız İPTAL EDİLMİŞ + hiç SMS gitmemiş kampanya için görünür.
  // Sunucu koşulları tek atomik UPDATE'te ayrıca doğruluyor — buradaki
  // görünürlük yalnız UX, güvenlik değil.
  const remove = async (id: string) => {
    if (deleting) return;
    if (!window.confirm(t("sms.deleteConfirm"))) return;
    setDeleting(id);
    try {
      await api.post(`/sms-campaigns/${id}/delete`);
      showToast(t("sms.deleted"));
      onCanceled();
    } catch (err) {
      showToast(err instanceof Error ? err.message : t("common.error"), false);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className={card}>
      <div className="px-5 py-3.5 border-b border-border bg-muted/30 flex items-center gap-2">
        <CalendarClock className="h-3.5 w-3.5 text-primary/60" />
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("sms.history")}</p>
      </div>

      {/* Tam-sayfa gate'i ilk yüklemeyi zaten beklediği için bu dal pratikte
          erişilemez; savunma amaçlı bırakıldı (bileşen gate'siz de çalışsın). */}
      {loading && campaigns.length === 0 ? (
        <div className="divide-y divide-border">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 px-5 py-4">
              <div className="flex-1 min-w-40 space-y-2">
                <div className="h-3.5 w-32 rounded-md bg-muted animate-pulse" />
                <div className="h-2.5 w-48 rounded-md bg-muted animate-pulse" />
              </div>
              <div className="h-2.5 w-10 rounded-md bg-muted animate-pulse" />
              <div className="h-5 w-16 rounded-full bg-muted animate-pulse" />
              <div className="h-6 w-6 rounded-lg bg-muted animate-pulse" />
            </div>
          ))}
        </div>
      ) : campaigns.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-14">
          <MessageSquareText className="h-8 w-8 text-primary/20" />
          <p className="text-sm text-muted-foreground">{t("sms.noCampaigns")}</p>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {campaigns.map((c) => {
            const holdReason = toHoldReason(c.hold_reason);
            const approval = toApprovalStatus(c.approval_status);
            return (
            <div key={c.id}>
              <div className="flex items-center gap-3 px-5 py-4 flex-wrap">
                <div className="flex-1 min-w-40">
                  <p className="text-sm font-semibold text-foreground">{c.name}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {segmentLabel(t, c.segment_type)} · {scheduleLabel(t, c.schedule_type)}
                    {/* ⚠️ Beklemedeyken `next_dispatch_at` GÖSTERİLMEZ. Hold, maliyet
                        engellerinde vadeyi geri-çekilme için ileri atar; kullanıcının
                        kurmadığı o yapay tarihi "sıradaki: …" diye göstermek 27 Tem
                        2026 olayında insanları yanılttı — açıklama rozeti gerçeği
                        zaten anlatıyor. */}
                    {!holdReason && c.next_dispatch_at && (c.status === "scheduled" || c.status === "active") && (
                      <> · {t("sms.nextRound", { date: new Date(c.next_dispatch_at + "Z").toLocaleString(locale) })}</>
                    )}
                  </p>
                  {holdReason && (
                    <p className="mt-1.5 text-[11px] leading-snug text-amber-700 dark:text-amber-400">
                      {holdReasonLabel(t, holdReason)}
                    </p>
                  )}
                  {/* Onay açıklaması hold'dan AYRI satır: ikisi ortogonal, biri
                      diğerinin yerine geçmez. `approved` SESSİZ — onaylanmış
                      kampanya organizatör için normal kampanyadır. */}
                  {approval === "pending" && (
                    <p className="mt-1.5 text-[11px] leading-snug text-indigo-700 dark:text-indigo-400">
                      {t("sms.approval.pendingHint", { count: c.approval_recipient_count ?? 0 })}
                    </p>
                  )}
                  {approval === "rejected" && (
                    <p className="mt-1.5 text-[11px] leading-snug text-rose-700 dark:text-rose-400">
                      {c.rejection_reason
                        ? t("sms.approval.rejectedHintWithReason", { reason: c.rejection_reason })
                        : t("sms.approval.rejectedHint")}
                    </p>
                  )}
                </div>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {t("sms.roundsUsed", { count: c.sends_used })}
                </span>
                {holdReason && (
                  <span
                    title={holdReasonLabel(t, holdReason)}
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ring-1 ring-inset bg-amber-500/10 text-amber-600 dark:text-amber-400 ring-amber-500/20"
                  >
                    <PauseCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
                    {t("sms.held")}
                  </span>
                )}
                {/* ⚠️ Renk AMBER OLAMAZ: amber "Beklemede" (hold) rozetinin rengi.
                    İki durum ortogonal ve aynı satırda YAN YANA görünebildiği
                    için ayırt edilebilir olmaları şart → indigo. */}
                {approval === "pending" && (
                  <span
                    title={t("sms.approval.pendingHint", { count: c.approval_recipient_count ?? 0 })}
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ring-1 ring-inset bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 ring-indigo-500/20"
                  >
                    <ShieldQuestion className="h-3 w-3 shrink-0" aria-hidden="true" />
                    {approvalStatusLabel(t, approval)}
                  </span>
                )}
                {approval === "rejected" && (
                  <span
                    title={c.rejection_reason ?? t("sms.approval.rejectedHint")}
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ring-1 ring-inset bg-rose-500/10 text-rose-600 dark:text-rose-400 ring-rose-500/20"
                  >
                    <ShieldX className="h-3 w-3 shrink-0" aria-hidden="true" />
                    {approvalStatusLabel(t, approval)}
                  </span>
                )}
                <span className={cn(
                  "px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ring-1 ring-inset",
                  STATUS_BADGE[c.status],
                )}>
                  {campaignStatusLabel(t, c.status)}
                </span>
                {(c.status === "scheduled" || c.status === "active") && (
                  <button type="button" onClick={() => cancel(c.id)} disabled={canceling === c.id}
                    title={t("sms.cancel")} aria-label={t("sms.cancel")}
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-rose-500 hover:bg-rose-500/10 transition-colors cursor-pointer disabled:opacity-40">
                    {canceling === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                  </button>
                )}
                {/* Silme: yalnız İPTAL EDİLMİŞ + hiç SMS gitmemiş kampanyada.
                    Sunucu aynı koşulları tek atomik UPDATE'te de doğruluyor. */}
                {c.status === "canceled" && c.sends_used === 0 && (
                  <button type="button" onClick={() => remove(c.id)} disabled={deleting === c.id}
                    title={t("sms.delete")} aria-label={t("sms.delete")}
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-rose-500 hover:bg-rose-500/10 transition-colors cursor-pointer disabled:opacity-40">
                    {deleting === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                )}
                <button type="button" onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                  aria-expanded={expanded === c.id} title={t("sms.details")} aria-label={t("sms.details")}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-primary/5 transition-colors cursor-pointer">
                  {expanded === c.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              </div>
              {expanded === c.id && <CampaignDetail api={api} slug={slug} id={c.id} locale={locale} />}
            </div>
            );
          })}
        </div>
      )}

      {nextCursor && !loading && (
        <div className="flex justify-center px-5 py-4 border-t border-border">
          <button type="button" onClick={onLoadMore}
            className="px-5 py-2 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:border-primary/30 hover:bg-primary/5 transition-all cursor-pointer">
            {t("common.loadMore")}
          </button>
        </div>
      )}
    </div>
  );
}

function CampaignDetail({ api, slug, id, locale }: {
  api: SmsCampaignsApi; slug: string; id: string; locale: string;
}) {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ["sms-campaign", slug, id],
    queryFn: async () => (await api.get<{ campaign: CampaignRow; rounds: RoundRow[] }>(`/sms-campaigns/${id}`)).data,
  });

  const [sends, setSends] = useState<SendRow[]>([]);
  const [sendsCursor, setSendsCursor] = useState<string | null>(null);
  const [sendsLoading, setSendsLoading] = useState(false);

  const loadSends = useCallback(async (cursor?: string) => {
    setSendsLoading(true);
    try {
      const res = await api.get<{ items: SendRow[]; nextCursor: string | null }>(
        cursor ? `/sms-campaigns/${id}/sends?cursor=${encodeURIComponent(cursor)}` : `/sms-campaigns/${id}/sends`,
      );
      const d = res.data as { items: SendRow[]; nextCursor: string | null };
      setSends((prev) => (cursor ? [...prev, ...d.items] : d.items));
      setSendsCursor(d.nextCursor);
    } catch { /* detail sends are non-critical; row simply stays empty */ }
    finally { setSendsLoading(false); }
  }, [api, id]);

  useEffect(() => { loadSends(); }, [loadSends]);

  if (isLoading) {
    return <div className="flex justify-center py-8 bg-muted/10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  }

  const rounds = (data as { rounds?: RoundRow[] } | undefined)?.rounds ?? [];

  return (
    <div className="px-5 pb-5 bg-muted/10 space-y-4">
      {/* Round summaries */}
      {rounds.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-4">
          {rounds.map((r) => (
            <div key={r.round_number} className="rounded-xl border border-border bg-card px-3.5 py-2.5 text-center">
              <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{t("sms.round", { n: r.round_number })}</p>
              <p className="text-xs mt-1 tabular-nums">
                <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{r.delivered + r.sent}</span>
                <span className="text-muted-foreground"> / {r.total}</span>
                {r.failed > 0 && <span className="text-rose-500 font-semibold ml-1.5">✕{r.failed}</span>}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Per-guest sends */}
      {sends.length > 0 && (
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="border-b border-border bg-muted/20">
                <tr>
                  {[t("sms.table.guest"), t("sms.table.phone"), t("sms.table.round"), t("sms.table.status")].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sends.map((s) => (
                  <tr key={s.id} className="bg-card">
                    <td className="px-4 py-2.5 text-sm text-foreground">{s.guest_name ?? "—"}</td>
                    <td className="px-4 py-2.5 text-xs font-mono text-muted-foreground">{s.phone_e164}</td>
                    <td className="px-4 py-2.5 text-xs tabular-nums text-muted-foreground">{s.round_number}</td>
                    <td className="px-4 py-2.5">
                      <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold uppercase", SEND_BADGE[s.status] ?? SEND_BADGE.queued)}>
                        {t(`sms.sendStatus.${s.status}`)}
                      </span>
                      {s.error_code && <span className="ml-1.5 text-[10px] font-mono text-rose-500">{s.error_code}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {sendsCursor && (
            <div className="flex justify-center px-4 py-3 border-t border-border bg-card">
              {sendsLoading
                ? <Loader2 className="h-4 w-4 animate-spin text-primary" />
                : <button type="button" onClick={() => loadSends(sendsCursor)}
                    className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                    {t("common.loadMore")}
                  </button>}
            </div>
          )}
        </div>
      )}

      {rounds.length === 0 && sends.length === 0 && !sendsLoading && (
        <p className="pt-4 text-sm text-muted-foreground">{t("sms.noSendsYet")}</p>
      )}
    </div>
  );
}
